import React, {Dispatch, SetStateAction, Suspense, useContext, useEffect, useState} from 'react';
import {faPause, faPlay, faPlus, faTint, faTrash, faWind} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BranchDevice, ShortDevicePassport, WeekDays, WorkHours } from '../../models/warehouse-models';
import Select, { SingleValue } from 'react-select';
import { NewRequestContext } from '../../context/NewRequestContext';
import { CustomButton } from '../CustomButton';
import {Button, Form, Modal, Spinner} from 'react-bootstrap';
import { Controller, FieldErrors, useForm } from 'react-hook-form';
import { deviceWorkHours } from '../../utils/Enums/NewRequest';
import { devicePassportsSelectStyles, shortOilPassportsSelectStyles } from '../../styles/selectStyles';
import { InfoModal } from '../InfoModal';
import {
    addNewRequestClientDevices,
    deleteNewRequestClientDevices,
    editNewRequestClientDevices, setDeviceActive, setDeviceInactive, useGetBranchPaymentInfo,
    useGetNewRequestClientDevices,
} from '../../utils/clients-hooks';
import { resourceCache, useAsyncResource } from 'use-async-resource';
import { PassportImages } from '../../pages/passports/PassportDetails';
import noImage from '../../assets/No_image_available.png';
import { CustomDropdown } from '../Dashboard/CustomDropdown';
import { useGetUsers } from '../../utils/users-hooks';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../CustomToastContent';
import ReactDatePicker from 'react-datepicker';
import { parseDateToTime, parseTimeToDate } from '../../utils/helper-functions';
import { CustomSelect } from '../CustomSelect';
import {useGetAllOilPassports, useGetReferencesForDistributor} from '../../utils/warehouses-hooks';
import { useGetAllTaskSkeletons } from '../../utils/work-hooks';
import { AuthContext } from '../../context/AuthContext';
import { Role } from '../../utils/Enums/Roles';
import {toastOptionsTopRight} from "../../utils/Enums/ToastOptions";
import {fetchMapBranches, fetchTasksAll, fetchTasksYours} from "../../utils/dashboard-hooks";
import {DatePickerWrapper} from "../DatePickerWrapper";


export const NewRequestDevice = ({
    device,
    isCreatingNew = false,
    setShowDeviceTemplate,
}: {
    device: BranchDevice;
    isCreatingNew?: boolean;
    setShowDeviceTemplate: Dispatch<SetStateAction<boolean>>;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'newRequest' });
    const { t: commonTranslation } = useTranslation('translation', { keyPrefix: 'common' });
    const [getDistributors] = useAsyncResource(useGetUsers, undefined, 'DISTRIBUTOR,INSTALLER');
    const distributors = getDistributors();

    const { selectedBranch, devicesPassports, getDevices, refetchTasks,getBranchPaymentInfo } = useContext(NewRequestContext);
    const { hasPermission,loggedUser } = useContext(AuthContext);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [currentDevice, setCurrentDevice] = useState<BranchDevice>(device);
    const [installNow, setInstallNow] = useState(false);
    const [isEditable, setIsEditable] = useState(false);
    const [showDatePick, setShowDatePick] = useState(false);
    const [date, setDate] = useState('');
    const [defaultWorkHours, setDefaultWorkHours] = useState(deviceWorkHours);
    const [distributor, setDistributor] = useState('');
    const [deleteReason, setDeleteReason] = useState('');
    const [freeTextReason, setFreeTextReason] = useState('');
    const [getAllOilPassports] = useAsyncResource(useGetAllOilPassports, false);
    const isDistributor = !hasPermission([Role.COORDINATOR]) && (hasPermission([Role.DISTRIBUTOR])||hasPermission([Role.INSTALLER]));

    useEffect(() => {
        if(isDistributor){
            setDistributor(String(loggedUser?.id));
        } else if(selectedBranch?.assignedDistributor){
            setDistributor(String(selectedBranch.assignedDistributor?.id));
        }
    }, [showDeleteModal]);

    const selectedDeviceOilPassports = getAllOilPassports().map((value) => ({
        ...value,
        oilId: value.id,
    }));
    const resolvedDevicePassport = devicesPassports.find((passport) => passport.id === currentDevice.passportId);
    const fallbackDevicePassportLabel =
        currentDevice.fullModelName?.trim() || currentDevice.deviceName?.trim() || currentDevice.model?.trim() || '';
    const selectedDevicePassport =
        resolvedDevicePassport ??
        (currentDevice.passportId > 0 && fallbackDevicePassportLabel
            ? {
                  id: currentDevice.passportId,
                  passportId: String(currentDevice.passportId),
                  name: fallbackDevicePassportLabel,
                  model: currentDevice.model ?? '',
                  type: 'DEVICE',
                  oilType: currentDevice.oilType ?? '',
                  sprayRange: defaultWorkHours.sprayRange,
                  waitRange: defaultWorkHours.waitRange,
                  imgs: [],
                  oils: [],
                  requireBattery: false,
              }
            : null);
    const {
        control,
        handleSubmit,
        getValues,
        reset,
        formState: { errors },
    } = useForm<BranchDevice>({
        defaultValues: currentDevice ,
    });
    const addSubmitHandler = async (formValue: BranchDevice) => {
        resourceCache(useGetNewRequestClientDevices).clear();
        resourceCache(useGetAllTaskSkeletons).clear();
        if (selectedBranch.id && loggedUser?.id) {
            const response = await addNewRequestClientDevices(formValue, selectedBranch.id, installNow);
            if (response !== undefined) {
                setShowDeviceTemplate(false);
                toast(<CustomToastContent content={[t(installNow ? 'deviceInstallSuccess' : 'deviceSuccess')]} />, {
                    position: 'top-right',
                    autoClose: 5000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    type: 'success',
                });
            }
            getDevices(selectedBranch.id);
        }
    };

    const editSubmitHandler = async (formValue: BranchDevice) => {
        resourceCache(useGetNewRequestClientDevices).clear();

        if (selectedBranch.id && currentDevice.id) {
            const selectedOil = selectedDeviceOilPassports.find(({ oilId }) => oilId == formValue.oilId);
            if (selectedOil) {
                formValue.oilName = selectedOil.name;
                formValue.oilType = selectedOil.oilType;
            }
            const response = await editNewRequestClientDevices(formValue, currentDevice.id, selectedBranch.id);
            if (response !== undefined) {
                setIsEditable(!isEditable);
                getDevices(selectedBranch.id);
            }
        }

        setCurrentDevice(formValue);
    };

    const deleteHandler = async (deviceId: number) => {
        const params = { deviceId, employeeId: distributor, state: deleteReason };
        const response = await deleteNewRequestClientDevices(
            deleteReason === 'freetext' ? { ...params, state: 'working', reason: freeTextReason } : params
        );

        if (response) {
            successHandler();

            setFreeTextReason('');
            setDeleteReason('');
            setDistributor('');
        }
    };

    const activateDevice = async (deviceId: number | undefined) => {
        const response = await setDeviceActive(deviceId);
        if (response) {
            successHandler();
        }
    }

    const deactivateDevice = async (deviceId: number | undefined) => {
        const response = await setDeviceInactive(deviceId,date);
        if (response) {
            successHandler();
        }
    }

    const successHandler = () => {
        toast.success(<CustomToastContent content={[t('success')]}/>, toastOptionsTopRight);
        resourceCache(useGetNewRequestClientDevices).clear();
        resourceCache(useGetReferencesForDistributor).clear();
        resourceCache(fetchTasksAll).clear();
        resourceCache(fetchTasksYours).clear();
        // REMOVED: resourceCache(fetchMapBranches).clear();
        // Device operations don't modify branch data, no need to refetch branches
        resourceCache(useGetBranchPaymentInfo).clear();
        if(refetchTasks)
            refetchTasks();
        selectedBranch.id && getDevices(selectedBranch.id);
        selectedBranch.id && getBranchPaymentInfo(selectedBranch.id);
    }

    const onDevicePassportChange = (newValue: SingleValue<ShortDevicePassport>) => {
        if (newValue) {
            newValue.id &&
                setCurrentDevice({
                    ...currentDevice,
                    passportId: newValue.id,
                });
            setDefaultWorkHours({
                ...defaultWorkHours,
                sprayRange: newValue.sprayRange,
                waitRange: newValue.waitRange,
            });
        }
    };

    useEffect(() => {
        setCurrentDevice({
            ...device,
        });
        reset(device);
    }, [device]);

    useEffect(() => {
        const devicePassport = devicesPassports.find((passport) => passport.id === device.passportId);

        if (devicePassport) {
            setDefaultWorkHours({
                ...defaultWorkHours,
                sprayRange: devicePassport.sprayRange,
                waitRange: devicePassport.waitRange,
            });
        }
    }, []);
    return (
        <div className='w-100 border mt-1 d-flex'>
            <div className={`border-end device-img d-flex justify-content-center align-items-center ${!device.active&&"bg-inactive-device"}`}>
                {currentDevice.passportId !== 0 ? (
                    <Suspense
                        fallback={
                            <div className='w-100 h-100 d-flex justify-content-center align-items-center'>
                                <Spinner animation={'border'} />
                            </div>
                        }
                    >
                        <PassportImages id={currentDevice.passportId} />
                    </Suspense>
                ) : (
                    <img className='passportImage noImg' src={noImage} alt={`passport img`} />
                )}
            </div>
            <div className={`d-flex flex-column device-configuration ${!device.active&&"bg-inactive-device"}`}>
                <Form
                    onSubmit={isEditable ? handleSubmit(editSubmitHandler) : handleSubmit(addSubmitHandler)}
                    id={`device-form${isCreatingNew ? '-new' : ''}${device.id || ''}`}
                >
                    <Form.Group className='align-content-center w-100' controlId='deviceName'>
                        <Form.Label  className='mb-0 px-3 me-1 '>
                            {currentDevice.fullModelName || currentDevice.deviceName || currentDevice.model}
                        </Form.Label>
                    </Form.Group>
                    <div className='d-flex ps-1 py-1 align-items-center w-100'>
                        <div className='d-flex align-items-center w-75'>
                            <Form.Group className='d-flex align-items-center w-50' controlId='passportId'>
                                <Form.Label className='mb-0 px-1 me-1'>{commonTranslation('Model')}</Form.Label>
                                <Controller
                                    rules={{ required: true, validate: (value) => value > 0 }}
                                    control={control}
                                    name='passportId'
                                    render={({ field }) => (
                                        <Select
                                            className={errors.passportId && 'border border-danger rounded'}
                                            onChange={(newValue) => {
                                                field.onChange(newValue?.id);
                                                onDevicePassportChange(newValue);
                                            }}
                                            isDisabled={!isCreatingNew}
                                            placeholder={'Choose Device'}
                                            styles={devicePassportsSelectStyles}
                                            value={selectedDevicePassport}
                                            options={devicesPassports}
                                            maxMenuHeight={150}
                                            getOptionLabel={({ name }) => name}
                                            getOptionValue={({ passportId }) => passportId}
                                        />
                                    )}
                                />
                            </Form.Group>
                            <Form.Group className='d-flex align-items-center w-50' controlId='oilId'>
                                <Form.Label className='mb-0 px-1 me-1'>{commonTranslation('Oil')}</Form.Label>
                                <Controller
                                    control={control}
                                    name='oilId'
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <Select
                                            className={errors.oilId && 'border border-danger rounded'}
                                            onChange={(newValue) => field.onChange(newValue?.oilId)}
                                            isDisabled={!(selectedDeviceOilPassports && (isCreatingNew || isEditable))}
                                            placeholder={'Choose Oil'}
                                            value={selectedDeviceOilPassports?.find(
                                                (passport) => passport.oilId === device.oilId
                                            )}
                                            styles={shortOilPassportsSelectStyles}
                                            options={selectedDeviceOilPassports}
                                            maxMenuHeight={150}
                                            getOptionLabel={({ name }) => name}
                                            getOptionValue={(option) => option.name}
                                        />
                                    )}
                                />
                            </Form.Group>
                        </div>
                        {device.id != undefined && ( device.active ?
                            <div>
                            <CustomButton
                                iconClass='text-danger'
                                variant='outline-none'
                                icon={faPause}
                                disabled={false}
                                onClick={() => setShowDatePick(true)}
                                buttonSize='lg'
                                className='background-unset delete-work-mode text-black m-0 px-1 pe-2 py-0 border-0'
                            />
                            <Modal show={showDatePick} onHide={()=>setShowDatePick(false)}>
                                <Modal.Header closeButton>
                                    <Modal.Title>{t('chooseDate')}</Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    <DatePickerWrapper
                                        isClearable={true}
                                        selected={date}
                                        onChange={(value) => setDate(value === 'cleared' ? '' : value)}
                                    />
                                    <Button
                                        onClick={() => {
                                            deactivateDevice(device.id);
                                        }}
                                        className='btn-danger m-2'
                                        disabled={date.length < 1}
                                    >
                                        {t('deactivateDevice')}
                                    </Button>
                                </Modal.Body>
                            </Modal>
                            </div>
                            :
                            <CustomButton
                                iconClass='text-danger'
                                variant='outline-none'
                                icon={faPlay}
                                disabled={false}
                                onClick={() => activateDevice(device.id)}
                                buttonSize='lg'
                                className='background-unset delete-work-mode text-black m-0 px-1 pe-2 py-0 border-0'
                            />)
                        }
                        {!isCreatingNew ? (
                            <div className='d-flex w-25 mx-1  justify-content-end'>
                                <div className='d-flex align-items-center'>
                                    <FontAwesomeIcon icon={faTint} color='green' size='sm' className='fa-fw' />
                                    <h6 className='mb-0 me-1'>
                                        {currentDevice.active ? currentDevice.oilPercent : '-'}%
                                    </h6>
                                </div>
                                <CustomButton
                                    icon={faTrash}
                                    iconClass='fa-fw'
                                    onClick={() => {
                                        setShowDeleteModal(true);
                                    }}
                                    className='color-main p-1 py-0'
                                />
                                <InfoModal
                                    show={showDeleteModal}
                                    isDisabled={distributor.length < 1 || deleteReason.length < 1}
                                    onHide={(isDelete: boolean) => {
                                        if (isDelete && currentDevice.id) {
                                            deleteHandler(currentDevice.id).then();
                                        }
                                        setFreeTextReason('');
                                        setDeleteReason('');
                                        return setShowDeleteModal(false);
                                    }}
                                >
                                    <span className='w-100 text-secondary'>{`*${t('fieldsRequired')}`}</span>

                                    {!isDistributor?<CustomDropdown
                                        entities={distributors.items.map((distributor) => {
                                            return {
                                                label: distributor.name,
                                                value: String(distributor.id),
                                            };
                                        })}
                                        label={t('distributor')}
                                        defaultValue={selectedBranch.assignedDistributor?.id}
                                        setEntities={setDistributor}
                                    />:<></>}
                                    <h5 className='w-100 mb-0 py-3'>{t('reason')}</h5>
                                    <CustomSelect
                                        customClass={'mb-3'}
                                        updateValue={(value) => setDeleteReason(value)}
                                        itemsToShow={['', 'damaged', 'defective', 'working', 'freetext']}
                                    />

                                    <Form.Control
                                        placeholder={commonTranslation('freetext')}
                                        className={`${deleteReason !== 'freetext' ? 'disabled' : ''}`}
                                        disabled={deleteReason !== 'freetext'}
                                        name={'freetext'}
                                        onChange={(e) => setFreeTextReason(e.currentTarget.value)}
                                        value={freeTextReason}
                                    />
                                </InfoModal>
                            </div>
                        ) : (
                            <Form.Label title={t('installDeviceNowTooltip')} className='d-flex'>
                                <label className='me-1'>{t('installDeviceNow')}</label>
                                <Form.Switch
                                    checked={installNow}
                                    onChange={(e) => setInstallNow(e.currentTarget.checked)}
                                />
                            </Form.Label>
                        )}
                    </div>
                    <Controller
                        control={control}
                        render={({ field }) => (
                            <WorkHoursInner
                                errors={errors}
                                onChange={(list) => {
                                    field.onChange(list);
                                }}
                                hasDevice={!!getValues('passportId')}
                                list={field.value}
                                isDisabled={!(isCreatingNew || isEditable)}
                            />
                        )}
                        name={'workHoursList'}
                    />
                </Form>
            </div>
        </div>
    );
};

export const WorkHoursInner = ({
    list,
    onChange,
    isDisabled,
    errors,
    hasDevice,
}: {
    hasDevice?: boolean;
    errors?: FieldErrors<BranchDevice>;
    list: WorkHours[];
    onChange: (list: WorkHours[]) => void;
    isDisabled?: boolean;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'newRequest' });

    const deleteField = (indexToRemove: number) => {
        onChange(list.filter((value, index) => index !== indexToRemove));
    };

    const addNewWorkHours = () => {
        onChange(
            list.concat({
                ...deviceWorkHours,
                weekDaysBoolean: { ...deviceWorkHours.weekDaysBoolean },
            })
        );
    };

    const changeField = (changeValue: (workHours: WorkHours) => WorkHours, workIndex: number) => {
        const newList = list.map((value, index) => {
            if (index === workIndex) {
                return changeValue(value);
            }
            return value;
        });
        onChange(newList);
    };
    const onDayToggle = (active: boolean, workIndex: number, day: keyof WeekDays) => {
        const newList = list.map((value, index) => {
            if (index === workIndex) {
                return { ...value, weekDaysBoolean: { ...value.weekDaysBoolean, [day]: active } };
            }
            return value;
        });
        onChange(newList);
    };
    const onStartTime = (startTime: string, workIndex: number) => {
        changeField((value) => ({ ...value, startTime }), workIndex);
    };
    const onEndTime = (endTime: string, workIndex: number) => {
        changeField((value) => ({ ...value, endTime }), workIndex);
    };

    const onSprayRange = (sprayRange: number, workIndex: number) => {
        changeField((value) => ({ ...value, sprayRange }), workIndex);
    };

    const onWaitRange = (waitRange: number, workIndex: number) => {
        changeField((value) => ({ ...value, waitRange }), workIndex);
    };

    return (
        <>
            {list.map((workHours, workIndex) => (
                <div className='d-flex border-top' key={`workhours${workIndex}`}>
                    <div className='d-flex'>
                        {Object.entries(workHours.weekDaysBoolean).map(([key, value], weekDayIndex) => (
                            <Form.Check
                                key={`weekday${weekDayIndex}`}
                                aria-label='option 1'
                                className='p-0 d-flex justify-content-center align-items-center flex-column px-1'
                            >
                                <Form.Check.Label>
                                    {t(`${key}`)}
                                    <Form.Check.Input
                                        onChange={(e) =>
                                            onDayToggle(e.currentTarget.checked, workIndex, key as keyof WeekDays)
                                        }
                                        disabled={isDisabled}
                                        type='checkbox'
                                        checked={value}
                                        className='m-0 mt-1'
                                    />
                                </Form.Check.Label>
                            </Form.Check>
                        ))}
                    </div>
                    <div className='d-flex w-50 device-work-days'>
                        <Form.Group className='d-flex align-items-center px-1 w-50' controlId='start'>
                            <Form.Label className='px-1 '>
                                <p className='mb-0'>Вкл.</p>
                            </Form.Label>
                            <ReactDatePicker
                                onChange={(e) => {
                                    e && onStartTime(parseDateToTime(e.toString()), workIndex);
                                }}
                                selected={parseTimeToDate(workHours.startTime, 'HH:mm')}
                                timeFormat='HH:mm'
                                showTimeSelect
                                showTimeSelectOnly
                                dateFormat='HH:mm'
                                className='border border-secondary text-center py-1 rounded w-100 '
                                onChangeRaw={(e) => {
                                    e.preventDefault();
                                }}
                                disabled={isDisabled}
                            />
                        </Form.Group>
                        <Form.Group className='d-flex align-items-center  px-1 w-50' controlId='start'>
                            <Form.Label className='px-1 position-relative d-flex justify-content-center'>
                                <p className='mb-0'>Изкл.</p>
                            </Form.Label>
                            <ReactDatePicker
                                onChange={(e) => {
                                    e && onEndTime(parseDateToTime(e.toString()), workIndex);
                                }}
                                selected={parseTimeToDate(workHours.endTime, 'HH:mm')}
                                timeFormat='HH:mm'
                                showTimeSelect
                                showTimeSelectOnly
                                dateFormat='HH:mm'
                                className='border border-secondary text-center py-1 rounded w-100 '
                                onChangeRaw={(e) => {
                                    e.preventDefault();
                                }}
                                disabled={isDisabled}
                            />
                        </Form.Group>
                    </div>
                    <div className='d-flex w-50 device-work-days'>
                        <Form.Group className='d-flex align-items-center px-1 w-50' controlId='start'>
                            <Form.Label className='px-1'>
                                <FontAwesomeIcon icon={faWind} />
                            </Form.Label>
                            <Form.Control
                                disabled={isDisabled || !hasDevice}
                                defaultValue={workHours.sprayRange}
                                type='text'
                                className={`w-75 p-0 text-center ${
                                    errors && errors.workHoursList?.[workIndex]?.sprayRange && 'border border-danger'
                                }`}
                                onChange={(e) => onSprayRange(+e.currentTarget.value, workIndex)}
                            />
                        </Form.Group>
                        <Form.Group className='d-flex align-items-center px-1 w-50' controlId='start'>
                            <Form.Label className='px-1 '>
                                <FontAwesomeIcon icon={faPause} />
                            </Form.Label>

                            <Form.Control
                                disabled={isDisabled || !hasDevice}
                                defaultValue={workHours.waitRange}
                                type='text'
                                className={`w-75 p-0 text-center ${
                                    errors && errors.workHoursList?.[workIndex]?.waitRange && 'border border-danger'
                                }`}
                                onChange={(e) => onWaitRange(+e.currentTarget.value, workIndex)}
                            />
                        </Form.Group>
                    </div>
                    {!isDisabled && (
                        <CustomButton
                            iconClass='text-danger'
                            variant='outline-none'
                            icon={faTrash}
                            disabled={isDisabled}
                            onClick={() => deleteField(workIndex)}
                            buttonSize='lg'
                            className='background-unset delete-work-mode text-black m-0 px-1 pe-2 py-0 border-0'
                        />
                    )}
                </div>
            ))}
            {list.length < 5 && (
                <div className='d-flex justify-content-center p-1 py-1 align-items-center border-top '>
                    <CustomButton
                        className='p-1 py-1'
                        iconClass='fa-fw'
                        variant='outline-success'
                        disabled={isDisabled || !hasDevice}
                        icon={faPlus}
                        onClick={addNewWorkHours}
                    />
                </div>
            )}
        </>
    );
};
